import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../shared/context/CartContext';
import { useWishlist } from '../../shared/context/WishlistContext';
import { useCartDrawer } from '../../shared/context/CartDrawerContext';

const NavContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.black};
  color: ${props => props.theme.colors.white};
  display: none;
  z-index: ${props => props.theme.zIndex.fixed};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 4px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
    padding: 3px 0;
  }
`;

const NavList = styled.ul`
  display: flex;
  justify-content: space-around;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
`;

const NavItem = styled.li`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NavLink = styled(Link) <{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px;
  text-decoration: none;
  color: ${props => props.$active ? props.theme.colors.white : 'rgba(255, 255, 255, 0.7)'};
  transition: color ${props => props.theme.transitions.base};
  position: relative;
  width: 100%;
  
  &:hover {
    color: ${props => props.theme.colors.white};
  }
  
  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    fill: none;
  }
`;

const NavButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px;
  text-decoration: none;
  color: ${props => props.$active ? props.theme.colors.white : 'rgba(255, 255, 255, 0.7)'};
  transition: color ${props => props.theme.transitions.base};
  position: relative;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  
  &:hover {
    color: ${props => props.theme.colors.white};
  }
  
  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    fill: none;
  }
`;

const NavLabel = styled.span`
  font-size: 7px;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const Badge = styled.span`
  position: absolute;
  top: 2px;
  inset-inline-end: calc(50% - 18px);
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.black};
  font-size: 9px;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
`;

const ShopIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
);

const WishlistIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const CartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

const AllProductsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const MobileBottomNav: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const { openDrawer: openCartDrawer, closeDrawer: closeCartDrawer } = useCartDrawer();

  const handleCartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openCartDrawer();
  };

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    closeCartDrawer(); // Close cart when searching
    navigate('/products?focus=search');
  };

  const navItems = [
    { path: '/products', label: t('nav.allProducts'), icon: AllProductsIcon, isLink: true },
    { path: '/wishlist', label: t('nav.wishlist'), icon: WishlistIcon, badge: wishlistCount, isLink: true },
    { path: '/', label: t('nav.home'), icon: ShopIcon, isLink: true },
    { path: '#', label: t('nav.cart'), icon: CartIcon, badge: itemCount, isLink: false, onClick: handleCartClick },
    { path: '/products?focus=search', label: t('nav.search'), icon: SearchIcon, isLink: false, onClick: handleSearchClick },
  ];

  return (
    <NavContainer>
      <NavList>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : item.path === '/products' || item.path === '/products?focus=search'
              ? location.pathname === '/products' || location.pathname.startsWith('/products/')
              : location.pathname === item.path;

          return (
            <NavItem key={item.path}>
              {item.isLink ? (
                <NavLink to={item.path} $active={isActive} onClick={closeCartDrawer}>
                  <div style={{ position: 'relative' }}>
                    <Icon />
                    {item.badge !== undefined && item.badge > 0 && (
                      <Badge>{item.badge}</Badge>
                    )}
                  </div>
                  <NavLabel>{item.label}</NavLabel>
                </NavLink>
              ) : (
                <NavButton onClick={item.onClick} $active={isActive}>
                  <div style={{ position: 'relative' }}>
                    <Icon />
                    {item.badge !== undefined && item.badge > 0 && (
                      <Badge>{item.badge}</Badge>
                    )}
                  </div>
                  <NavLabel>{item.label}</NavLabel>
                </NavButton>
              )}
            </NavItem>
          );
        })}
      </NavList>
    </NavContainer>
  );
};

export default MobileBottomNav;

